import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {makeAutoObservable, observable, IReactionDisposer, computed} from "mobx";
import type {IGameBar} from "data/stores/game_bar/game_bar.store";
import {compact, isEqual, sumBy} from "lodash";
import type {IContestPlayerStatsStore} from "data/stores/contest_player_stats/contest_player_stats.store";
import {Empty} from "data/types/global";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import {ContestStatus, GameLevel, RequestState} from "data/enums";
import type {IPlayersStore} from "data/stores/players/players.store";
import type {IRosterItem} from "data/types/roster";
import type {IGamePlayer, IPlayerCalculations} from "data/utils/player.calculations";

interface IParams {
	contestId?: number;
	gamebar?: IGameBar;
	roster?: IRosterItem;
}

export interface IGameBarController extends ViewController<IParams> {
	get i18n(): ILocalizationStore;
	get isLoading(): boolean;
	get gameBar(): IGameBar | undefined;
	get levelString(): string;
	get level(): GameLevel;
	get isComplete(): boolean;
	get projPts(): Empty<number>;
	get dailyPts(): string;
	get dailyRank(): string;
	get weeklyRank(): string;
	get weeklyPts(): string;
	get contestStatus(): ContestStatus;
}

@injectable()
export class GameBarController implements IGameBarController {
	@observable private _contestId?: number;
	@observable private _roster?: IRosterItem;
	@observable private _gamebar?: IGameBar;
	@observable private _requestState: RequestState = RequestState.IDLE;

	@observable _reactionDisposers: IReactionDisposer[] = [];

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ContestsStore) private readonly _contestStore: IContestsStore,
		@inject(Bindings.ContestPlayerStatsStore)
		private readonly _contestPlayerStatsStore: IContestPlayerStatsStore,
		@inject(Bindings.PlayerCalculations)
		private readonly _playerCalculations: IPlayerCalculations,
		@inject(Bindings.PlayersStore) private readonly _playersStore: IPlayersStore
	) {
		makeAutoObservable(this);
	}

	get isLoading() {
		return this._requestState === RequestState.PENDING;
	}

	get projPts() {
		const players = this.players;
		if (!players.length) {
			return;
		}
		return sumBy(players, (rosterPlayer) => {
			return rosterPlayer?.contestStats?.projectedPoints || 0;
		});
	}

	private get rosterPlayers() {
		return this._roster?.players || [];
	}

	@computed private get players(): IGamePlayer[] {
		return compact(
			this.rosterPlayers.map((it) => {
				return this._playerCalculations.getGamePlayerById(it.playerId, this._contestId);
			})
		);
	}

	@computed private get liveDailyPts() {
		const points = sumBy(
			this.players,
			(rosterPlayer) => rosterPlayer.contestStats?.fantasyPoints ?? 0
		);

		return `${points || "--"}`;
	}

	get dailyPts(): string {
		const isLive = isEqual(this.contestStatus, ContestStatus.Live);

		if (isLive) {
			return this.liveDailyPts;
		}

		const points = this.gameBar?.contestPoints;
		return points ? String(points) : "--";
	}

	get dailyRank(): string {
		const rank = this.gameBar?.contestRank;
		return rank ? String(rank) : "--";
	}

	get weeklyRank(): string {
		const rank = this.gameBar?.roundRank;
		return rank ? String(rank) : "--";
	}

	get weeklyPts(): string {
		const points = this.gameBar?.roundPoints;
		return points ? String(points) : "--";
	}

	get levelString(): string {
		const level = this.level;
		return this.i18n.t(`league.level.${level}`, level);
	}

	get level() {
		return this.gameBar?.level || GameLevel.SINGLE_A;
	}

	get isComplete(): boolean {
		return this.currentContest?.status === ContestStatus.Complete;
	}

	get contestStatus(): ContestStatus {
		return this.currentContest?.status || ContestStatus.Open;
	}

	get gameBar() {
		return this._gamebar;
	}

	private get currentContest() {
		if (this._contestId) {
			return this._contestStore.getContestById(this._contestId);
		}
	}
	dispose(): void {
		this._reactionDisposers.forEach((dispose) => dispose());
	}

	init(param: IParams): void {
		this._contestId = param.contestId;
		this._roster = param.roster;
		this._gamebar = param.gamebar;
	}

	onChange(param: IParams) {
		this.init(param);
	}
}
